import * as THREE from 'three'
import { RunConfig } from '../../config/runs'
import { getElevation } from '../../world/terrain/SlopeTerrain'

/**
 * Creates a finish line banner at the end of each run.
 * Two poles with a banner stretched between them, plus checkered flags.
 */

export function createFinishLine(config: RunConfig): THREE.Group {
  const finish = new THREE.Group()

  // Position at the runout area (85% of run length)
  const finishZ = config.length / 2 - config.length * 0.08
  const groundY = getElevation(0, finishZ)
  const bannerWidth = config.width * 0.6
  const poleHeight = 5

  // Left pole
  const poleMat = new THREE.MeshStandardMaterial({ color: 0xcc0000, roughness: 0.4 })
  const leftPole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.08, poleHeight, 8),
    poleMat
  )
  leftPole.position.set(-bannerWidth / 2, groundY + poleHeight / 2, finishZ)
  leftPole.castShadow = true
  finish.add(leftPole)

  // Right pole
  const rightPole = leftPole.clone()
  rightPole.position.set(bannerWidth / 2, groundY + poleHeight / 2, finishZ)
  finish.add(rightPole)

  // Banner (checkered pattern using multiple boxes)
  const bannerHeight = 1.2
  const bannerY = groundY + poleHeight - bannerHeight / 2 - 0.3
  const checkerSize = bannerHeight / 3
  const checkerCount = Math.floor(bannerWidth / checkerSize)

  const whiteMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.3 })
  const blackMat = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.3 })

  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < checkerCount; col++) {
      const isBlack = (row + col) % 2 === 0
      const checker = new THREE.Mesh(
        new THREE.BoxGeometry(checkerSize, checkerSize, 0.05),
        isBlack ? blackMat : whiteMat
      )
      const x = -bannerWidth / 2 + checkerSize / 2 + col * checkerSize
      const y = bannerY + (row - 1) * checkerSize
      checker.position.set(x, y, finishZ)
      finish.add(checker)
    }
  }

  // "FINISH" text backing (red banner behind the checkers)
  const backingMat = new THREE.MeshStandardMaterial({ color: 0xcc0000, roughness: 0.4 })
  const backing = new THREE.Mesh(
    new THREE.BoxGeometry(bannerWidth + 0.5, bannerHeight + 0.4, 0.03),
    backingMat
  )
  backing.position.set(0, bannerY, finishZ - 0.04)
  finish.add(backing)

  // Cross bar on top
  const crossBar = new THREE.Mesh(
    new THREE.CylinderGeometry(0.05, 0.05, bannerWidth + 0.6, 8),
    poleMat
  )
  crossBar.rotation.z = Math.PI / 2
  crossBar.position.set(0, groundY + poleHeight - 0.2, finishZ)
  finish.add(crossBar)

  // Line on the snow (white stripe across the run)
  const lineMat = new THREE.MeshStandardMaterial({ color: 0xff0000, roughness: 0.3 })
  const snowLine = new THREE.Mesh(
    new THREE.BoxGeometry(bannerWidth + 2, 0.02, 0.4),
    lineMat
  )
  snowLine.position.set(0, groundY + 0.02, finishZ)
  finish.add(snowLine)

  return finish
}

export function getFinishLineZ(config: RunConfig): number {
  return config.length / 2 - config.length * 0.08
}
